//imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import UnderlineHeading from "../../../../components/underlineHeading";
import Card from "../../../../components/card";
import { HolidayDTO, HolidayAffectedScheduleDTO } from "../../../../models/HolidayDTO";
import { AcademicCalendarViewProps } from "./interfaces";
import { fechaBonita } from "../../../../utils/fechaBonita";
import { horarioBonito } from "../../../../utils/horarioBonito";

//styles
import "../../../../components/filtrosRapidos/filtrosRapidos.css";
import "./styles.css";

const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;

/* "12 horarios · 26 clases recorridas"; modo 2 = clase repuesta; sin nada = "Sin cambios". */
const efecto = (afectados: HolidayAffectedScheduleDTO[] | null | undefined): string => {
	if (!afectados || afectados.length === 0) return "Sin cambios";

	const recorridas = afectados.filter(a => a.modeId === 1).reduce((acc, a) => acc + (a.movedClasses || 0), 0);
	const repuestas = afectados.filter(a => a.modeId === 2).length;
	const otros = afectados.filter(a => a.modeId !== 1 && a.modeId !== 2);

	const partes: string[] = [plural(afectados.length, "horario", "horarios")];
	if (recorridas > 0) partes.push(plural(recorridas, "clase recorrida", "clases recorridas"));
	if (repuestas > 0) partes.push(plural(repuestas, "clase repuesta", "clases repuestas"));
	if (otros.length > 0) {
		const etiquetas = Array.from(new Set(otros.map(o => o.modeLabel).filter(Boolean)));
		if (etiquetas.length > 0) partes.push(etiquetas.join(", ").toLowerCase());
	}

	return partes.join(" · ");
};

const AcademicCalendarView = ({
	sectionLoaded,
	campusName,
	idleLocked,
	allowed,
	years,
	selectedYears,
	onToggleYear,
	onAddYear,
	search,
	onSearch,
	holidays,
	onDelete,
	form,
	onFormChange,
	scheduleOptions,
	loadingSchedules,
	saving,
	canSubmit,
	onSubmit,
	embebido
}: AcademicCalendarViewProps) => {

	if (!sectionLoaded) return <></>;

	const bloqueado = idleLocked || saving;

	return (
		<div className={embebido ? "calendarioContainer embebido" : "calendarioContainer"}>
			{
				embebido ? null :
					<>
						<div className="calendarioEncabezado">
							<h2 className="pageHeader">Asuetos</h2>
							{campusName ? <span className="calendarioPlantel">{campusName}</span> : null}
						</div>
						<UnderlineHeading />
					</>
			}

			<div className="waFiltros">
				<div className="waFiltrosFila">
					<div className="waBuscador">
						<FontAwesomeIcon icon={solid("magnifying-glass")} />
						<input
							type="search"
							value={search}
							placeholder="Buscar motivo u horario"
							disabled={idleLocked}
							onChange={e => onSearch(e.target.value)}
						/>
						{
							!search ? null :
								<button type="button" onClick={() => onSearch("")} aria-label="Limpiar búsqueda">
									<FontAwesomeIcon icon={solid("xmark")} />
								</button>
						}
					</div>
					<div className="waChips">
						{
							years.map(anio => {
								const activo = selectedYears.includes(anio);
								return (
									<button
										key={anio}
										type="button"
										className={activo ? "waChipFiltro naranja activo" : "waChipFiltro naranja"}
										aria-pressed={activo}
										disabled={idleLocked}
										onClick={() => onToggleYear(anio)}
									>
										{activo ? <FontAwesomeIcon icon={solid("check")} /> : null}
										{anio}
									</button>
								);
							})
						}
						<button
							type="button"
							className="waChipFiltro naranja masAnio"
							title="Abrir un año más"
							aria-label="Abrir un año más"
							disabled={idleLocked}
							onClick={onAddYear}
						>
							<FontAwesomeIcon icon={solid("plus")} />
							Año
						</button>
					</div>
					<span className="waCuenta">
						{holidays.length === 1 ? "1 asueto" : `${holidays.length} asuetos`}
					</span>
				</div>
			</div>

			{
				!allowed.create ? null :
					<div className="calendarioAlta">
						<div className="calendarioAltaTitulo">Nuevo Asueto</div>
						<div className="calendarioAltaCampos">
							<div className="calendarioCampo">
								<span>Fecha</span>
								<input
									type="date"
									className="form-control"
									value={form.date}
									disabled={bloqueado}
									onChange={e => onFormChange({ date: e.target.value })}
								/>
							</div>
							<div className="calendarioCampo crece">
								<span>Motivo</span>
								<input
									type="text"
									className="form-control"
									value={form.name}
									maxLength={120}
									placeholder="Ej. Día de la Independencia"
									disabled={bloqueado}
									onChange={e => onFormChange({ name: e.target.value })}
									onKeyDown={e => { if (e.key === "Enter" && canSubmit) onSubmit(); }}
								/>
							</div>
							<div className="calendarioCampo">
								<span>Tipo</span>
								<div className="calendarioOpciones">
									<label className="calendarioRadio">
										<input
											type="radio"
											name="holidayType"
											checked={form.typeId === 1}
											disabled={bloqueado}
											onChange={() => onFormChange({ typeId: 1 })}
										/>
										Oficial
									</label>
									<label className="calendarioRadio">
										<input
											type="radio"
											name="holidayType"
											checked={form.typeId === 2}
											disabled={bloqueado}
											onChange={() => onFormChange({ typeId: 2 })}
										/>
										Especial
									</label>
								</div>
							</div>
							<div className="calendarioCampo">
								<span>Alcance</span>
								<div className="calendarioOpciones">
									<label className="calendarioRadio">
										<input
											type="radio"
											name="holidayScope"
											checked={form.scope === "campus"}
											disabled={bloqueado}
											onChange={() => onFormChange({ scope: "campus" })}
										/>
										Todo el plantel
									</label>
									<label className="calendarioRadio">
										<input
											type="radio"
											name="holidayScope"
											checked={form.scope === "schedule"}
											disabled={bloqueado}
											onChange={() => onFormChange({ scope: "schedule" })}
										/>
										Un horario
									</label>
								</div>
							</div>
							{
								form.scope !== "schedule" ? null :
									<label className="calendarioCampo crece">
										<span>Horario</span>
										<select
											className="form-select"
											value={form.scheduleId == null ? "" : String(form.scheduleId)}
											disabled={bloqueado || loadingSchedules}
											onChange={e => onFormChange({ scheduleId: e.target.value ? Number(e.target.value) : null })}
										>
											<option value="">
												{
													loadingSchedules ? "Cargando…"
														: scheduleOptions.length === 0 ? "Sin clases ese día"
														: "Elige un horario"
												}
											</option>
											{
												scheduleOptions.map(s =>
													<option key={s.id} value={String(s.id)}>
														{
															[s.courseName, s.levelName].filter(Boolean).join(" · ")
															+ " — " + horarioBonito(s.name)
															+ (s.mentorName ? " · " + s.mentorName : "")
														}
													</option>
												)
											}
										</select>
									</label>
							}
							<label className="calendarioCampo crece">
								<span>Notas</span>
								<input
									type="text"
									className="form-control"
									value={form.notes}
									maxLength={255}
									placeholder="Opcional"
									disabled={bloqueado}
									onChange={e => onFormChange({ notes: e.target.value })}
								/>
							</label>
							<div className="calendarioCampo accion">
								<button
									type="button"
									className="waBotonPrincipal"
									disabled={bloqueado || !canSubmit}
									onClick={onSubmit}
								>
									<FontAwesomeIcon icon={solid("plus")} />
									<span className="ps-2">Agregar Asueto</span>
								</button>
							</div>
						</div>
					</div>
			}

			<Card>
				<div className="containerTable col-lg-12">
					{
						holidays.length === 0
							? <div className="calendarioVacio">Sin asuetos en este periodo.</div>
							:
							<table className="specialTable calendarioTabla">
								<thead>
									<tr>
										<th>Fecha</th>
										<th>Motivo</th>
										<th>Alcance</th>
										<th>Tipo</th>
										<th>Efecto</th>
										{allowed.delete ? <th></th> : null}
									</tr>
								</thead>
								<tbody>
									{
										holidays.map((h: HolidayDTO) =>
											<tr key={h.id}>
												<td className="calendarioFecha">{fechaBonita(h.date)}</td>
												<td>
													<div className="calendarioMotivo">{h.name}</div>
													{h.notes ? <div className="calendarioNota">{h.notes}</div> : null}
													{
														!h.retroactive ? null :
															<span className="waPill oro">Registrado hacia atrás</span>
													}
												</td>
												<td>{h.scheduleId == null ? "Todo el plantel" : (h.scheduleName ? horarioBonito(h.scheduleName) : `Horario ${h.scheduleId}`)}</td>
												<td>
													<span className={h.typeId === 1 ? "waPill ink" : "waPill suave"}>
														{h.typeLabel || (h.typeId === 1 ? "Oficial" : "Especial")}
													</span>
												</td>
												<td className="calendarioEfecto">{efecto(h.affectedSchedules)}</td>
												{
													!allowed.delete ? null :
														<td className="calendarioAcciones">
															<button
																type="button"
																className="btn btn-default waBotonIcono"
																title="Borrar asueto"
																aria-label="Borrar asueto"
																disabled={idleLocked}
																onClick={() => onDelete(h)}
															>
																<FontAwesomeIcon icon={solid("trash-can")} />
															</button>
														</td>
												}
											</tr>
										)
									}
								</tbody>
							</table>
					}
				</div>
			</Card>
		</div>
	);
}

export default AcademicCalendarView
